<template>
  <div class="message-page">
    <StepProgress />

    <SaveAuthModal
      v-model="showAuthModal"
      @authenticated="handleAuthenticated"
      @cancel="showAuthModal = false"
    />

    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Message
            </v-card-title>
            
            <v-card-text class="pa-6">
              <!-- Introduction Text -->
              <div class="mb-6">
                <p class="text-h6 mb-4">
                  To encourage those who knew {{ store.personName || 'your loved one' }} to share their photos and stories, 
                  we find it helps to have a personal message.
                </p>
              </div>

              <!-- Demo Video -->
              <div class="mb-8">
                <v-card class="demo-video-card" elevation="0">
                  <div class="video-container">
                    <video
                      ref="demoVideo"
                      class="demo-video"
                      playsinline
                      controls
                    >
                      <source src="https://assets.forever-x.com/static/video-encouragement.mp4" type="video/mp4">
                      <!-- Placeholder for demo video -->
                    </video>
                  </div>
                </v-card>
              </div>

              <!-- Existing Server Video Display -->
              <v-expand-transition>
                <div v-if="hasStoredVideo && store.videoMessage?.isServerVideo && !showVideoRecording && !showTextMessage" class="mb-6">
                  <v-card class="recording-card" elevation="2">
                    <v-card-title class="pb-2">
                      <v-icon left color="primary">mdi-video</v-icon>
                      Your Video Message
                    </v-card-title>

                    <v-card-text>
                      <div class="video-recording-container">
                        <video
                          v-if="store.videoMessage.url"
                          :src="store.videoMessage.url"
                          class="video-preview"
                          controls
                          playsinline
                        ></video>

                        <div class="recording-controls mt-4">
                          <v-btn
                            color="primary"
                            @click="playServerVideo"
                            class="mr-4"
                          >
                            <v-icon left>mdi-play</v-icon>
                            Review
                          </v-btn>

                          <v-btn
                            color="secondary"
                            variant="outlined"
                            @click="startVideoRecording"
                          >
                            <v-icon left>mdi-refresh</v-icon>
                            Re-record Video
                          </v-btn>

                          <v-btn
                            color="secondary"
                            variant="outlined"
                            @click="chooseTextMessage"
                          >
                            <v-icon left>mdi-pencil</v-icon>
                            Switch to Text Message
                          </v-btn>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Recording Options -->
              <div v-if="!hasStoredVideo || showVideoRecording || showTextMessage" class="mb-6">
                <h3 class="text-h5 mb-4">Choose Your Message Option</h3>

                <v-card class="option-card mb-4" elevation="0">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <h4 class="text-h6 mb-2">Record a Video Message</h4>
                        <p class="text-body-2 text--secondary">
                          Record a personal video message that will be shown to family and friends
                        </p>
                      </div>
                      <v-btn
                        color="primary"
                        @click="startVideoRecording"
                        :disabled="isRecording"
                      >
                        {{ recordButtonLabel }}
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <v-card class="option-card" elevation="0">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <h4 class="text-h6 mb-2">Write a Text Message</h4>
                        <p class="text-body-2 text--secondary">
                          Write a personal message that will be displayed to family and friends
                        </p>
                      </div>
                      <v-btn
                        color="secondary"
                        variant="outlined"
                        @click="chooseTextMessage"
                      >
                        No, Write Message
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>

              <!-- Video Recording Interface -->
              <v-expand-transition>
                <div v-if="showVideoRecording" ref="videoRecordingCard" class="mb-6">
                  <v-card class="recording-card" elevation="2">
                    <v-card-title class="pb-2">
                      <v-icon left color="error">mdi-video</v-icon>
                      Video Recording
                    </v-card-title>
                    
                    <v-card-text>
                      <div class="video-recording-container">
                        <video
                          ref="videoPreview"
                          class="video-preview"
                          :class="{ 'recording': isRecording }"
                          autoplay
                          :muted="isRecording || !recordedVideo"
                          playsinline
                          controls
                        ></video>
                        
                        <div class="recording-controls mt-4">
                          <v-btn
                            v-if="!isRecording && !recordedVideo"
                            color="error"
                            size="large"
                            @click="startRecording"
                            class="mr-4"
                          >
                            <v-icon left>mdi-record</v-icon>
                            Start Recording
                          </v-btn>
                          
                          <v-btn
                            v-if="isRecording"
                            color="error"
                            size="large"
                            @click="stopRecording"
                            class="mr-4"
                          >
                            <v-icon left>mdi-stop</v-icon>
                            Stop Recording
                          </v-btn>
                          
                          <v-btn
                            v-if="recordedVideo"
                            color="primary"
                            @click="playRecording"
                            class="mr-4"
                          >
                            <v-icon left>mdi-play</v-icon>
                            Review
                          </v-btn>
                          
                          <v-btn
                            v-if="recordedVideo"
                            color="secondary"
                            variant="outlined"
                            @click="retakeRecording"
                          >
                            <v-icon left>mdi-refresh</v-icon>
                            Re-record
                          </v-btn>

                          <v-btn
                            v-if="recordedVideo"
                            color="secondary"
                            variant="outlined"
                            @click="chooseTextMessage"
                          >
                            <v-icon left>mdi-pencil</v-icon>
                            Changed my Mind, Write a Message
                          </v-btn>
                        </div>
                        
                        <div v-if="isRecording" class="recording-indicator mt-2">
                          <v-chip color="error" class="pulse">
                            <v-icon left>mdi-circle</v-icon>
                            Recording {{ recordingTime }}
                          </v-chip>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Text Message Interface -->
              <v-expand-transition>
                <div v-if="showTextMessage">
                  <v-card class="text-message-card" elevation="2">
                    <v-card-title class="pb-2">
                      <v-icon left color="primary">mdi-message-text</v-icon>
                      Personal Message
                    </v-card-title>
                    
                    <v-card-text class="mt-3">
                      <v-textarea
                        v-model="personalMessage"
                        label="Write your personal message here..."
                        variant="outlined"
                        rows="6"
                        auto-grow
                        :rules="messageRules"
                        placeholder="Share why this memorial is important to you and encourage others to contribute their own memories..."
                      ></v-textarea>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="previousStep"
                class="mr-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>

              <v-spacer></v-spacer>

              <!-- Save button (visible when not logged in OR when logged in with changes) -->
              <v-btn
                v-if="!isEditMode || (isEditMode && hasChanges)"
                variant="outlined"
                color="primary"
                size="large"
                :disabled="!canProceed || saving || (isEditMode && !hasChanges)"
                @click="handleSave"
                class="mr-3"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save
              </v-btn>

              <!-- Next Step button (always visible when not in edit mode, OR when in edit mode but hasn't visited Preview page yet) -->
              <v-btn
                v-if="!isEditMode || (isEditMode && !hasVisitedPreviewPage)"
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                :disabled="!canProceed || saving"
                @click="nextStep"
                class="px-8"
              >
                Next Step
                <v-icon right class="ml-2">mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Permission Dialog -->
    <v-dialog v-model="showPermissionDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 bg-error text-white pa-4">
          <v-icon left color="white">mdi-video-off</v-icon>
          Camera/Microphone Access Required
        </v-card-title>
        <v-card-text class="pa-6">
          <p style="white-space: pre-line;">{{ permissionInstructions }}</p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="elevated"
            @click="showPermissionDialog = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="showErrorDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 bg-error text-white pa-4">
          <v-icon left color="white">mdi-alert-circle</v-icon>
          Error Saving Memory
        </v-card-title>
        <v-card-text class="pa-6">
          <p>{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="elevated"
            @click="showErrorDialog = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import StepProgress from '../components/StepProgress.vue'
import SaveAuthModal from '../components/SaveAuthModal.vue'
import apiService from '../services/api'

const router = useRouter()
const store = useMemorialStore()

// Refs
const demoVideo = ref(null)
const videoPreview = ref(null)
const videoRecordingCard = ref(null)

// State
const showVideoRecording = ref(false)
const showTextMessage = ref(false)
const isRecording = ref(false)
const recordedVideo = ref(null)
const personalMessage = ref('')
const recordingTime = ref('00:00')
const mediaRecorder = ref(null)
const mediaStream = ref(null)
const saving = ref(false)
const showPermissionDialog = ref(false)
const permissionInstructions = ref('')
const showErrorDialog = ref(false)
const errorMessage = ref('')
const showAuthModal = ref(false)

// Recording timer
let recordingTimer = null
let recordingStartTime = 0
let storedVideoObjectUrl = null

// Validation rules
const messageRules = [
  v => !v || v.length <= 10000 || 'Message is too long'
]

// Computed
const hasStoredVideo = computed(() => !!store.videoMessage)

const canProceed = computed(() => {
  return (recordedVideo.value !== null) ||
         hasStoredVideo.value ||
         (personalMessage.value && personalMessage.value.trim().length > 0)
})

const isEditMode = computed(() => {
  // User is in edit mode if they are logged in (completed account creation)
  // AND have an existing memorial they're editing
  // First-time users walking through the steps should see "Next Step" even with memorial UUID
  return store.isAccountCreated && store.isEmailVerified && store.memorialUuid
})

const hasVisitedPreviewPage = computed(() => {
  // Message is step 3, Preview is step 4
  // If user's maxReachedStep is greater than 3, they've visited Preview page
  return store.currentStep > 3 || store.maxReachedStep > 3
})

const hasChanges = computed(() => store.hasUnsavedChangesComputed)

const recordButtonLabel = computed(() => {
  if (isRecording.value) {
    return 'Recording...'
  }

  if (hasStoredVideo.value) {
    return 'Re-record Video'
  }

  return 'Yes, Record Video'
})

// Methods
const pauseDemoVideo = () => {
  if (demoVideo.value && !demoVideo.value.paused) {
    demoVideo.value.pause()
  }
}

const startVideoRecording = async () => {
  pauseDemoVideo()
  showVideoRecording.value = true
  showTextMessage.value = false
  await nextTick()
  setTimeout(() => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
  }, 600)

  try {
    // Check if permissions API is available
    if (navigator.permissions) {
      try {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' })
        const microphonePermission = await navigator.permissions.query({ name: 'microphone' })

        if (cameraPermission.state === 'denied' || microphonePermission.state === 'denied') {
          showPermissionDeniedMessage()
          return
        }
      } catch (permError) {
        // Permissions API might not support camera/microphone on all browsers
        console.log('Permissions API not fully supported, proceeding with getUserMedia')
      }
    }

    mediaStream.value = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true
    })
    videoPreview.value.srcObject = mediaStream.value
  } catch (error) {
    console.error('Error accessing camera:', error)

    // Check if it's a permission error
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      showPermissionDeniedMessage()
    } else {
      // Other error (no camera available, etc.)
      permissionInstructions.value = 'Unable to access camera or microphone. Please check that your device has these features and try again.'
      showPermissionDialog.value = true
      chooseTextMessage()
    }
  }
}

const showPermissionDeniedMessage = () => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)
  const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor)
  const isFirefox = /Firefox/.test(navigator.userAgent)

  let instructions = ''

  if (isMac && isSafari) {
    instructions = 'Safari on Mac:\n1. Click Safari menu > Settings\n2. Go to Websites > Camera and Microphone\n3. Allow access for this website\n4. Refresh the page and try again'
  } else if (isMac && isChrome) {
    instructions = 'Chrome on Mac:\n1. Click the lock icon in the address bar\n2. Change Camera and Microphone permissions to "Allow"\n3. Refresh the page and try again'
  } else if (isChrome) {
    instructions = 'Chrome:\n1. Click the lock or camera icon in the address bar\n2. Change Camera and Microphone permissions to "Allow"\n3. Refresh the page and try again'
  } else if (isFirefox) {
    instructions = 'Firefox:\n1. Click the camera icon in the address bar\n2. Remove the blocked permissions\n3. Refresh the page and try again'
  } else {
    instructions = 'Please check your browser settings to allow camera and microphone access for this website, then refresh and try again.'
  }

  permissionInstructions.value = `Camera and microphone access denied.\n\n${instructions}\n\nAlternatively, you can close this dialog and choose to write a text message instead.`
  showPermissionDialog.value = true

  // Fallback to text message
  chooseTextMessage()
}

const chooseTextMessage = () => {
  pauseDemoVideo()
  showTextMessage.value = true
  showVideoRecording.value = false
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => track.stop())
  }
  // Only clear video if user is actively switching from a locally recorded video
  // Don't clear if there's a server video (isServerVideo: true)
  if (recordedVideo.value && !store.videoMessage?.isServerVideo) {
    recordedVideo.value = null
    store.updateVideoMessage(null)
  }
}

const startRecording = () => {
  if (!mediaStream.value) return

  const chunks = []

  // Configure MediaRecorder with options to ensure audio is captured
  let options = { mimeType: 'video/webm;codecs=vp8,opus' }

  // Fallback to default if the specified mimeType is not supported
  if (!MediaRecorder.isTypeSupported(options.mimeType)) {
    console.log('vp8,opus not supported, trying vp9,opus')
    options = { mimeType: 'video/webm;codecs=vp9,opus' }

    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      console.log('vp9,opus not supported, using default')
      options = { mimeType: 'video/webm' }
    }
  }

  mediaRecorder.value = new MediaRecorder(mediaStream.value, options)

  mediaRecorder.value.ondataavailable = (event) => {
    if (event.data.size > 0) {
      chunks.push(event.data)
    }
  }

  mediaRecorder.value.onstop = () => {
    const blob = new Blob(chunks, { type: 'video/webm' })
    recordedVideo.value = {
      blob,
      url: URL.createObjectURL(blob)
    }

    // Automatically play the recorded video for review
    setTimeout(() => {
      playRecording()

      // Update store after video is ready so hasChanges is triggered
      const videoFile = buildVideoFileFromRecording()
      if (videoFile) {
        store.updateVideoMessage(videoFile)
      }
    }, 100)
  }

  mediaRecorder.value.start()
  isRecording.value = true
  recordingStartTime = Date.now()

  recordingTimer = setInterval(() => {
    const elapsed = Math.floor((Date.now() - recordingStartTime) / 1000)
    const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0')
    const seconds = (elapsed % 60).toString().padStart(2, '0')
    recordingTime.value = `${minutes}:${seconds}`
  }, 1000)
}

const stopRecording = () => {
  if (mediaRecorder.value && isRecording.value) {
    mediaRecorder.value.stop()
    isRecording.value = false
    
    if (recordingTimer) {
      clearInterval(recordingTimer)
      recordingTimer = null
    }
  }
}

const playRecording = () => {
  if (recordedVideo.value && videoPreview.value) {
    videoPreview.value.srcObject = null
    videoPreview.value.src = recordedVideo.value.url
    videoPreview.value.muted = false // Ensure audio is enabled for playback
    videoPreview.value.play().catch(err => {
      console.error('Error playing recorded video:', err)
    })
  }
}

const retakeRecording = () => {
  if (storedVideoObjectUrl) {
    URL.revokeObjectURL(storedVideoObjectUrl)
    storedVideoObjectUrl = null
  }
  if (recordedVideo.value?.url && recordedVideo.value.url.startsWith('blob:')) {
    URL.revokeObjectURL(recordedVideo.value.url)
  }
  recordedVideo.value = null
  if (mediaStream.value) {
    videoPreview.value.srcObject = mediaStream.value
  }
  recordingTime.value = '00:00'
  store.updateVideoMessage(null)
}

const playServerVideo = () => {
  // Find the server video element and play it
  const serverVideoElement = document.querySelector('.video-recording-container video')
  if (serverVideoElement) {
    serverVideoElement.play().catch(err => {
      console.error('Error playing server video:', err)
    })
  }
}

// Navigation
const nextStep = () => {
  pauseDemoVideo()
  // Save to store
  if (recordedVideo.value) {
    const videoFile = buildVideoFileFromRecording()
    if (videoFile) {
      store.updateVideoMessage(videoFile)
    }
  }
  if (personalMessage.value) {
    store.updatePersonalMessage(personalMessage.value)
  }

  store.goToStep(4) // Preview step
  router.push('/preview')
}

const previousStep = () => {
  pauseDemoVideo()
  store.goToStep(2) // Photos step
  router.push('/photos')
}

const handleSave = () => {
  // If user is not logged in, show auth modal
  if (!store.isAccountCreated || !store.isEmailVerified || !store.apiToken) {
    showAuthModal.value = true
  } else {
    // User is logged in, save directly
    return saveMemorialFromButton()
  }
}

const handleAuthenticated = async (authResponse) => {
  // User has authenticated, now save the memorial

  // Update store with user data
  store.setApiAuth(authResponse.token, authResponse.user)

  // Now save the memorial
  return await saveMemorialFromButton()
}

const saveMemorialFromButton = async () => {
  if (!canProceed.value || saving.value) return

  saving.value = true

  try {
    // First save current message/video to store if changed
    if (recordedVideo.value) {
      const videoFile = buildVideoFileFromRecording()
      if (videoFile) {
        store.updateVideoMessage(videoFile)
      }
    }
    if (personalMessage.value) {
      store.updatePersonalMessage(personalMessage.value)
    }

    // Handle host video upload if needed
    let hostVideoMessageUuid = null
    if (store.videoMessage && store.memorialUuid) {
      if (store.videoMessage instanceof File) {
        try {
          console.log('Uploading new host video...')
          const videoResponse = await apiService.uploadVideoMessage(
            store.memorialUuid,
            store.videoMessage,
            '',
            store.apiToken
          )

          if (videoResponse.success && videoResponse.mediaUuid) {
            hostVideoMessageUuid = videoResponse.mediaUuid
            console.log('Host video uploaded successfully:', hostVideoMessageUuid)
          } else {
            throw new Error(videoResponse.msg || 'Failed to upload video')
          }
        } catch (error) {
          console.error('Host video upload error:', error)
          throw error
        }
      } else if (store.videoMessage.uuid || store.videoMessage.isServerVideo) {
        hostVideoMessageUuid = store.videoMessage.uuid
        console.log('Including existing host video:', hostVideoMessageUuid)
      }
    }

    const reachedStep = Math.max(store.maxReachedStep, store.currentStep || 0)
    store.maxReachedStep = reachedStep

    // Build memorial data from store
    const memorialData = {
      uuid: store.memorialUuid,
      userUuid: store.user?.uuid || null,
      adminUuids: null,
      nameOfPerson: store.personName || null,
      memorialDate: store.memorialDate || null,
      address: store.memorialAddress || null,
      details: store.memorialDetails || null,
      maxReachedStep: reachedStep,
      mediaUuids: null,
      stories: buildStoriesArray(),
      contacts: buildContactsArray(),
      hostMessage: store.personalMessage || null,
      hostVideoMessageUuid: hostVideoMessageUuid,
      stripeCustomerId: null
    }


    // Use create if no UUID exists, otherwise edit
    const response = store.memorialUuid
      ? await apiService.editMemorial(memorialData, store.apiToken)
      : await apiService.createMemorial(memorialData, store.apiToken)

    if (!response.success) {
      throw new Error(response.msg || 'Failed to save memory')
    }

    console.log('Memorial saved successfully:', response)
    await store.persistMaxReachedStep()

    // Store the memorial UUID if this was a new memorial
    if (response.memorial?.uuid && !store.memorialUuid) {
      store.setMemorialUuid(response.memorial.uuid)
      if (response.memorial.shareSlug) {
        store.setShareSlug(response.memorial.shareSlug)
      }
    }

    // Upload profile photo if present
    if (store.profilePhotoFile && store.memorialUuid) {
      try {
        console.log('Uploading profile photo...')
        const profilePhotoResponse = await apiService.uploadPhoto(
          store.memorialUuid,
          store.profilePhotoFile,
          '', // No caption for profile photo
          store.apiToken,
          {
            isProfilePhoto: true
          }
        )

        if (!profilePhotoResponse.success) {
          throw new Error(profilePhotoResponse.msg || 'Failed to upload profile photo')
        }

        console.log('Profile photo uploaded successfully:', profilePhotoResponse)
      } catch (profilePhotoError) {
        console.error('Profile photo upload error:', profilePhotoError)
        // Continue even if profile photo fails
      }
    }

    // Upload regular photos if present
    if (store.photos.length > 0 && store.memorialUuid) {
      console.log(`Uploading ${store.photos.length} photos...`)

      for (const photo of store.photos) {
        if (photo.file) {
          try {
            console.log('Uploading photo:', photo.name)
            const uploadResponse = await apiService.uploadPhoto(
              store.memorialUuid,
              photo.file,
              photo.caption || '',
              store.apiToken
            )

            if (uploadResponse.success && uploadResponse.mediaUuid) {
              console.log('Photo uploaded successfully:', uploadResponse.mediaUuid)
            } else {
              console.error('Photo upload failed:', uploadResponse.msg || 'Unknown error')
            }
          } catch (error) {
            console.error('Photo upload error:', error)
          }
        }
      }
    }

    // Update change tracking after successful save
    store.markChangesSaved()

    return { success: true, memorial: response.memorial }

  } catch (error) {
    console.error('Memorial save error:', error)
    errorMessage.value = error.message || 'An error occurred while saving. Please try again.'
    showErrorDialog.value = true
    return { success: false, error: error.message }
  } finally {
    saving.value = false
  }
}

// eslint-disable-next-line no-unused-vars
const saveMemorial = async () => {
  if (!canProceed.value || saving.value) return

  saving.value = true
  
  try {
    // First save current message/video to store if changed
    if (recordedVideo.value) {
      const videoFile = buildVideoFileFromRecording()
      if (videoFile) {
        store.updateVideoMessage(videoFile)
      }
    }
    if (personalMessage.value) {
      store.updatePersonalMessage(personalMessage.value)
    }
    
    // Handle host video - both existing server video and new uploads
    let hostVideoMessageUuid = null
    if (store.videoMessage && store.memorialUuid) {
      if (store.videoMessage instanceof File) {
        // This is a new video that needs to be uploaded
        try {
          console.log('Uploading new host video...')
          const videoResponse = await apiService.uploadVideoMessage(store.memorialUuid, store.videoMessage, '', store.apiToken)
          
          if (videoResponse.success && videoResponse.mediaUuid) {
            hostVideoMessageUuid = videoResponse.mediaUuid
            console.log('Host video uploaded successfully:', hostVideoMessageUuid)
          } else {
            throw new Error(videoResponse.msg || 'Failed to upload video')
          }
        } catch (error) {
          console.error('Host video upload error:', error)
          throw error
        }
      } else if (store.videoMessage.uuid || store.videoMessage.isServerVideo) {
        // This is an existing video from the server
        hostVideoMessageUuid = store.videoMessage.uuid
        console.log('Including existing host video:', hostVideoMessageUuid)
      }
    }
    
    const reachedStep = Math.max(store.maxReachedStep, store.currentStep || 0)
    store.maxReachedStep = reachedStep

    // Build memorial data from store
    const memorialData = {
      uuid: store.memorialUuid,
      userUuid: store.user?.uuid || null,
      adminUuids: null,
      nameOfPerson: store.personName || null,
      address: store.memorialAddress || null,
      details: store.memorialDetails || null,
      maxReachedStep: reachedStep,
      mediaUuids: null, // Photos handled in PhotosPage
      stories: buildStoriesArray(),
      contacts: buildContactsArray(),
      hostMessage: store.personalMessage || null,
      hostVideoMessageUuid: hostVideoMessageUuid,
      stripeCustomerId: null
    }

    
    const response = await apiService.editMemorial(memorialData, store.apiToken)
    
    if (!response.success) {
      throw new Error(response.msg || 'Failed to save memory')
    }
    
    console.log('Memorial saved successfully:', response)
    await store.persistMaxReachedStep()
    
    // Update change tracking after successful save
    store.markChangesSaved()
    
    // Show success message or redirect
    // For now, just log success and stay on page
    
  } catch (error) {
    console.error('Memorial save error:', error)
    errorMessage.value = error.message || 'An error occurred while saving. Please try again.'
    showErrorDialog.value = true
  } finally {
    saving.value = false
  }
}

const buildStoriesArray = () => {
  return store.stories
    .filter(story => story && story.trim())
    .map(story => ({
      uuid: null,
      text: story.trim()
    }))
}

const buildContactsArray = () => {
  return store.contacts
    .filter(contact => contact && contact.trim())
    .map(contact => {
      const contactData = {
        uuid: null,
        email: null,
        phone: null,
        firstName: null,
        surname: null
      }
      
      // Check if it's an email or phone
      if (/.+@.+\..+/.test(contact.trim())) {
        contactData.email = contact.trim()
      } else if (/^[+]?[\s\-()]*([0-9][\s\-()]*){7,}/.test(contact.trim())) {
        contactData.phone = contact.trim()
      }
      
      return contactData
    })
    .filter(contact => contact.email || contact.phone) // Only include valid contacts
}

const buildVideoFileFromRecording = () => {
  if (!recordedVideo.value || !recordedVideo.value.blob) {
    return null
  }

  const blob = recordedVideo.value.blob

  if (typeof File !== 'undefined' && blob instanceof File) {
    return blob
  }

  return new File([blob], 'host-message.webm', {
    type: 'video/webm',
    lastModified: Date.now()
  })
}

// Watchers
watch(personalMessage, (newValue) => {
  store.updatePersonalMessage(newValue)
})

// Event handlers
const handlePageClick = (event) => {
  // Check if click is on a button or interactive element (not video controls)
  const target = event.target
  const isButton = target.tagName === 'BUTTON' ||
                   target.closest('button') ||
                   target.closest('.v-btn') ||
                   target.closest('a')

  // Don't pause if clicking on video controls
  const isVideoControl = target.closest('.demo-video-card')

  if (isButton && !isVideoControl) {
    pauseDemoVideo()
  }
}

// Lifecycle
onMounted(async () => {
  // If user has a memorial UUID, reload from server to ensure fresh data
  if (store.memorialUuid && store.isAccountCreated && store.isEmailVerified) {
    console.log('Reloading memorial data from server...')
    const loaded = await store.loadUserMemorialFromServer()
    if (loaded) {
      console.log('Memorial data reloaded successfully')
    }
  }

  personalMessage.value = store.personalMessage
  store.goToStep(3) // Set current step to Message

  // Initialize page tracking for logged-in users
  if (store.isAccountCreated && store.isEmailVerified && store.apiToken) {
    store.enterPage('message')
  }

  const hasSavedMessage = !!(store.personalMessage && store.personalMessage.trim())
  const hasVideoMessage = !!(store.videoMessage)

  // Priority: Show text message if it exists (but don't hide server video)
  // Server videos are shown in their own section above
  if (hasSavedMessage && !hasVideoMessage) {
    chooseTextMessage()
  } else if (hasVideoMessage && typeof File !== 'undefined' && store.videoMessage instanceof File) {
    // This is a locally recorded video (File object) - show in recording interface
    showVideoRecording.value = true
    showTextMessage.value = false

    if (storedVideoObjectUrl) {
      URL.revokeObjectURL(storedVideoObjectUrl)
    }
    storedVideoObjectUrl = URL.createObjectURL(store.videoMessage)
    recordedVideo.value = {
      blob: store.videoMessage,
      url: storedVideoObjectUrl
    }

    // Display the recorded video in the preview (but don't auto-play)
    setTimeout(() => {
      if (videoPreview.value && recordedVideo.value) {
        videoPreview.value.srcObject = null
        videoPreview.value.src = recordedVideo.value.url
        // Don't auto-play when returning to the page
        videoPreview.value.pause()
      }
    }, 100)
  }
  // Server videos (isServerVideo: true) will be displayed in the dedicated section above

  // Only attempt to play the demo video if user hasn't progressed past this page
  // Check if they've been to a later step (Contacts is step 4, Message is step 3)
  const hasProgressedPastMessage = store.currentStep > 3

  if (demoVideo.value && !hasProgressedPastMessage && !hasSavedMessage && !hasVideoMessage) {
    try {
      await demoVideo.value.play()
    } catch (error) {
      console.log('Autoplay with sound blocked by browser, video will need manual play:', error)
    }
  }

  // Add click listener to pause video on any button click
  document.addEventListener('click', handlePageClick)
})

// Add navigation guard to pause video when leaving page
onBeforeRouteLeave(() => {
  pauseDemoVideo()
})

onUnmounted(() => {
  // Remove click listener
  document.removeEventListener('click', handlePageClick)

  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => track.stop())
  }
  if (recordingTimer) {
    clearInterval(recordingTimer)
  }
  if (storedVideoObjectUrl) {
    URL.revokeObjectURL(storedVideoObjectUrl)
    storedVideoObjectUrl = null
  }
  if (recordedVideo.value?.url && recordedVideo.value.url.startsWith('blob:')) {
    URL.revokeObjectURL(recordedVideo.value.url)
  }
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;
.message-page {
  @include page-container;
}

.main-card {
  @include main-card;
  
  .v-card-title {
    @include section-header;
  }
}

.demo-video-card {
  @include card-base;
  overflow: hidden;
}

.video-container {
  position: relative;
  background: var(--v-theme-black);
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.demo-video {
  width: 100%;
  height: auto;
}

.video-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  background: rgba(0,0,0,0.7);
  padding: 2rem;
  border-radius: 12px;
}

.option-card {
  @include card-base;
  @include card-hover;
}

.recording-card,
.text-message-card {
  @include card-base;
}

.video-recording-container {
  text-align: center;
}

.video-preview {
  width: 100%;
  max-width: 500px;
  height: 300px;
  background: var(--v-theme-black);
  border-radius: 8px;
  object-fit: cover;
}

.video-preview.recording {
  border: 3px solid var(--v-theme-recording);
  box-shadow: 0 0 20px rgba(var(--v-theme-recording-rgb), 0.5);
}

.recording-controls {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.recording-indicator {
  display: flex;
  justify-content: center;
}

.pulse {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

@include mobile {
  .pa-6 {
    padding: 1rem !important;
  }
  
  .video-preview {
    height: 200px;
  }
  
  .recording-controls {
    flex-direction: column;
    align-items: center;
  }
}
</style>
